import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import normalize from '../../utils/normalize';
import SelectItems from '../create/select-items';

const CreateMaterial = () => {
  const router = useRouter();
  const { step } = router?.query;
  const storedInformation = typeof window !== 'undefined' ? localStorage.getItem('lobbyingMaterialInProgress') : null;
  const [createState, setCreateState] = useState({});
  const { headline } = createState;
  const { user } = useUser();
  const updateFromLocalStorage = useRef();
  const [disabledInput, setDisabledInput] = useState(false);

  updateFromLocalStorage.current = () => {
    let storedItem = JSON.parse(storedInformation);
    router.replace({ query: { ...router.query, step: 1 } });
    setCreateState({ ...createState, headline: storedItem?.headline ?? '', type: storedItem?.type });
    setType(storedItem?.type ?? 'Briefing material');
  };

  useEffect(() => {
    updateFromLocalStorage.current();
  }, []);

  const modifyState = ({ property, newValue }) => {
    setCreateState({ ...createState, [property]: newValue });
  };

  const changeStep = () => {
    router.push({ query: { step: 2 } });
    setLocalStorage({ property: 'type', newValue: type });
  };

  const setLocalStorage = ({ property, newValue }) => {
    let stateObject = !!storedInformation ? JSON.parse(storedInformation) : {};
    stateObject[property] = newValue;
    localStorage.setItem('lobbyingMaterialInProgress', JSON.stringify(stateObject));
  };

  const finishCreateProcess = () => {
    setLocalStorage({ property: 'headline', newValue: headline });
    router.push(
      user
        ? `/${normalize(type)}/create/2`
        : {
            pathname: '/auth/login',
            search: `?fragment=register${!!localStorage.getItem('lobbyingMaterialInProgress') ? `&returnTo=${`/${normalize(type)}/create/2`}` : ''}`,
          }
    );
  };

  const [type, setType] = useState('Briefing material');
  const stepOne = step === '1' || !step;
  const [route, setRoute] = useState('Create with AI');
  const selectedMaterialType = JSON.parse(storedInformation)?.type?.toLowerCase() || type.toLowerCase();
  const stepHeaderCopy = stepOne
    ? 'What type of lobbying material would you like to add?'
    : step === '2'
      ? `How would you like to create your ${selectedMaterialType}?`
      : `Give your ${selectedMaterialType} a headline`;

  return (
    <div className='max-w-[41rem] mx-auto mt-12'>
      {!user && (
        <div className='rounded-t-lg px-2 flex justify-between mb-3'>
          <h2 className='text-lg text-left font-bold'>
            {stepHeaderCopy}
          </h2>
          <p className='hidden lg:block text-gray-dark'>
            {step ?? 1} of {route === 'Create with AI' ? '2' : '3'}
          </p>
        </div>
      )}

      <div className={`px-5 border rounded-lg border-purple-100 bg-white min-h-[90px] shadow-md`}>
        <div className={`flex pt-6 pb-4 lg:pb-6`}>
          {stepOne && (
            <div className='overflow-auto pb-3 lg:pb-0'>
              <SelectItems
                items={['Briefing material', 'Policy ask', 'Research', 'News release']}
                selectedOption={type}
                setOption={setType}
              />
            </div>
          )}
          {step === '2' && (
            <div className='grow text-blue-dark pb-3 lg:pb-0 overflow-auto'>
              <SelectItems
                items={['Create with AI', 'Upload existing work']}
                selectedOption={route}
                setOption={setRoute}
              />
            </div>
          )}
          {step === '3' && (
            <HeadlineStep
              headline={headline}
              finishCreateProcess={finishCreateProcess}
              setDisabledInput={setDisabledInput}
              modifyState={modifyState}
            />
          )}
        </div>
      </div>

      <div className={`flex ${stepOne ? 'justify-end' : 'justify-between'} pt-8 w-full`}>
        <button
          className={`block w-full text-md rounded transition-colors px-6 py-2 text-white transition-colors cursor-pointer disabled:cursor-auto disabled:bg-gray-300 disabled:text-gray-dark bg-purple-100 hover:bg-purple-hover`}
          disabled={(step === '3' && headline === '') || disabledInput}
          onClick={() => {
            if (stepOne) {
              changeStep();
            } else if (step === '2') {
              if (route === 'Create with AI') {
                router.push(
                  user
                    ? { pathname: `/create/ai-assistant/${normalize(type)}` }
                    : {
                        pathname: '/auth/login',
                        search: `?fragment=register${!!localStorage.getItem('lobbyingMaterialInProgress') ? `&returnTo=${`/create/ai-assistant/${normalize(type)}`}` : ''}`,
                      }
                );
              } else {
                router.push({ query: { step: 3 } });
              }
            } else {
              finishCreateProcess();
            }
          }}
        >
          Next <span className='icon-long-arrow-right inline-block text-sm ml-2' />
        </button>
      </div>
    </div>
  );
};

const HeadlineStep = (props) => {
  const { headline, modifyState, finishCreateProcess, setDisabledInput } = props;
  const searchTimeout = useRef(null);

  const inputBox = useRef(null);
  return (
    <div className='grow pt-1 px-4'>
      <input
        autoFocus={true}
        value={headline}
        ref={inputBox}
        onChange={(e) => {
          modifyState({ property: 'headline', newValue: e.target.value });
        }}
        onKeyDown={(e) => {
          if (e.keyCode === 13 && e.target.value !== '') {
            finishCreateProcess();
          }
        }}
        onKeyPress={(e) => {
          setDisabledInput(true);
          if (searchTimeout.current) clearTimeout(searchTimeout.current);
          searchTimeout.current = setTimeout(() => {
            setDisabledInput(false);
          }, 500);
        }}
        className='bg-transparent w-full placeholder:text-base focus:outline-none text-lg text-blue-dark placeholder:text-gray-400'
        type='text'
        placeholder='Type your headline...'
      />
    </div>
  );
};

export default CreateMaterial;
